"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import { Box, Flex, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import {
  agentService,
  type FunctionConfig,
} from "../../../voice-services/agentService";
import { AgentFunctions } from "./AgentFunctions";

interface AgentFunctionsWrapperProps {
  agentId: string;
  llmId: string;
  initialFunctions: FunctionConfig[];
  /** Optional: render with a card shell for full-page usage */
  variant?: "bare" | "card";
}

export const AgentFunctionsWrapper: React.FC<AgentFunctionsWrapperProps> = ({
  agentId,
  llmId,
  initialFunctions,
  variant = "bare",
}) => {
  const [functions, setFunctions] =
    useState<FunctionConfig[]>(initialFunctions);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const cardBg = "white";
  const cardBorder = "gray.200";
  const pageBg = "gray.50";

  const fetchFunctions = useCallback(async () => {
    setIsLoading(true);
    try {
      const llmData = await agentService.getRetellLLM(llmId);
      if (llmData?.general_tools) {
        setFunctions(llmData.general_tools);
      }
    } catch (error) {
      console.error("Error fetching functions:", error);
      toast({
        title: "Error fetching functions",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [llmId, toast]);

  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  const content = isLoading ? (
    <Flex h="100%" w="100%" justify="center" align="center">
      <Spinner size="md" color="primary.500" />
    </Flex>
  ) : (
    <AgentFunctions
      agentId={agentId}
      llmId={llmId}
      functions={functions}
      onUpdate={async (updated: FunctionConfig[]) => {
        try {
          await agentService.updateRetellLLM(llmId, { general_tools: updated });

          setFunctions(updated);

          toast({
            title: "Functions updated successfully",
            status: "success",
            duration: 2000,
          });
          return { success: true };
        } catch (error: any) {
          console.error("Error updating functions:", error);
          let errorMessage =
            error.message || "Failed to save the function. Please try again.";

          if (error.isCalComValidationError) {
            errorMessage =
              "The Cal.com API key does not have access to the event type ID, or one of your credentials is invalid. Please verify your Cal.com API key and Event Type ID.";
          }

          toast({
            title: error.isCalComValidationError
              ? "Cal.com Validation Error"
              : "Error updating functions",
            description: errorMessage,
            status: "error",
            duration: 8000,
            isClosable: true,
          });

          await fetchFunctions();

          return { success: false, error: errorMessage };
        }
      }}
      isLoading={isLoading}
    />
  );

  if (variant === "card") {
    // Full-page / wide layout (like the original app)
    return (
      <Box minH="100vh" bg={pageBg}>
        <VStack
          spacing={6}
          align="stretch"
          maxW="7xl"
          mx="auto"
          py={6}
          px={{ base: 4, md: 6 }}
        >
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={cardBorder}
            borderRadius="xl"
            p={4}
            shadow="sm"
          >
            {content}
          </Box>
        </VStack>
      </Box>
    );
  }

  // Sidebar / accordion rail (no page chrome)
  return (
    <VStack spacing={4} align="stretch" w="full">
      <Box>{content}</Box>
    </VStack>
  );
};
